import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RefreshCw, XCircle, Clock, TrendingDown, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHECKPOINT_COLORS = {
  45: 'bg-slate-100 text-slate-700', 30: 'bg-blue-100 text-blue-800',
  15: 'bg-amber-100 text-amber-800', 7:  'bg-orange-100 text-orange-800',
  1:  'bg-red-100 text-red-800',
};

const LostLeads = () => {
  const [data, setData] = useState({ rows: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [waOpen, setWaOpen] = useState(false);
  const [waTarget, setWaTarget] = useState(null);
  const [waBody, setWaBody] = useState('');
  const [waSending, setWaSending] = useState(false);

  const openWa = async (row) => {
    // Resolve phone number: for policy kind, look up client; for lead kind, use lead phone.
    let phone = null;
    let name = row.name;
    try {
      if (row.kind === 'lead') {
        const { data: l } = await axios.get(`${API}/leads/${row.id}`, { withCredentials: true });
        phone = l.phone;
      } else {
        // policy: fetch full policy to get client_id then client mobile
        const { data: pAll } = await axios.get(`${API}/policies`, { withCredentials: true });
        const p = (pAll || []).find(x => x.policy_id === row.id);
        if (p) {
          const { data: c } = await axios.get(`${API}/clients/${p.client_id}`, { withCredentials: true });
          phone = c.primary_mobile;
          name = c.name || name;
        }
      }
    } catch { /* ignore, phone stays null */ }
    if (!phone) { toast.error('No mobile number on file'); return; }
    setWaTarget({ ...row, phone, name });
    setWaBody(`Hi ${name}, this is your agent from InsureGrowth. I wanted to check back in with you. `);
    setWaOpen(true);
  };
  const sendWa = async () => {
    if (!waTarget?.phone || !waBody.trim()) return;
    setWaSending(true);
    try {
      await axios.post(`${API}/messages/send`, { to: waTarget.phone, body: waBody, channel: 'whatsapp' }, { withCredentials: true });
      toast.success('WhatsApp sent');
      setWaOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setWaSending(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/lost-leads`, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (path, row, verb) => {
    const key = `${row.kind}-${row.id}`;
    setBusyId(key);
    try {
      await axios.post(`${API}/lost-leads/${path}`, { kind: row.kind, id: row.id }, { withCredentials: true });
      toast.success(verb);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusyId(''); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="lost-leads-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Lost Leads</h1>
        <p className="text-slate-600 mt-1 text-sm">Lapsed policies + unconverted leads. Reconnect ahead of next year's anniversary.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-red-50 border border-red-100 p-3"><p className="text-[11px] uppercase text-slate-500">Total</p><p className="text-2xl font-bold text-red-800">{data.total}</p></div>
        <div className="rounded-lg bg-amber-50 border border-amber-100 p-3"><p className="text-[11px] uppercase text-slate-500">Lapsed policies</p><p className="text-2xl font-bold text-amber-800">{data.counts?.policies || 0}</p></div>
        <div className="rounded-lg bg-purple-50 border border-purple-100 p-3"><p className="text-[11px] uppercase text-slate-500">Lost leads</p><p className="text-2xl font-bold text-purple-800">{data.counts?.leads || 0}</p></div>
      </div>

      <Card className="border-slate-200">
        <CardHeader><CardTitle style={{ fontFamily: 'Manrope' }}><TrendingDown className="inline h-5 w-5 mr-2" />Records</CardTitle></CardHeader>
        <CardContent>
          {data.rows.length === 0 ? (
            <p className="text-center text-slate-500 py-8" data-testid="lost-leads-empty">Nothing lost yet. Anything marked Lapsed (policy) or Lost (lead) will show up here for win-back.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Type</TableHead><TableHead>Name</TableHead><TableHead>What was lost</TableHead>
                <TableHead>Lost on</TableHead><TableHead>Next checkpoint</TableHead>
                <TableHead className="text-right">Premium</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.rows.map(r => {
                  const cp = r.next_checkpoint;
                  const cpClass = cp ? CHECKPOINT_COLORS[cp.offset] || 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-500';
                  return (
                    <TableRow key={`${r.kind}-${r.id}`} data-testid={`lost-row-${r.kind}-${r.id}`} className={r.winback_stopped ? 'opacity-60' : ''}>
                      <TableCell><Badge className={r.kind === 'policy' ? 'bg-amber-100 text-amber-800' : 'bg-purple-100 text-purple-800'}>{r.kind === 'policy' ? 'Lapsed Policy' : 'Lost Lead'}</Badge></TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-sm text-slate-700">{r.what_lost}</TableCell>
                      <TableCell className="text-xs text-slate-500">{r.lost_at}</TableCell>
                      <TableCell>
                        {r.winback_stopped ? <Badge className="bg-slate-200 text-slate-600">Stopped</Badge> :
                          cp ? (
                            <div className="flex items-center gap-1.5">
                              <Badge className={cpClass}><Clock className="h-3 w-3 mr-1 inline" />in {cp.days_until}d</Badge>
                              <span className="text-[10px] text-slate-500">({cp.offset}d before {r.next_anniversary})</span>
                            </div>
                          ) : '—'}
                      </TableCell>
                      <TableCell className="text-right">{r.premium ? formatCurrency(r.premium) : '—'}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => openWa(r)} data-testid={`lost-wa-${r.kind}-${r.id}`} className="border-green-600 text-green-700 hover:bg-green-50 mr-1">
                          <MessageSquare className="h-3 w-3 mr-1" /> WhatsApp
                        </Button>
                        {!r.winback_stopped && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => act('reconnect', r, 'Reconnected — back to active')}
                              disabled={busyId === `${r.kind}-${r.id}`} data-testid={`lost-reconnect-${r.kind}-${r.id}`} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 mr-1">
                              <RefreshCw className="h-3 w-3 mr-1" /> Reconnect
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => act('permanently-lost', r, 'Marked permanently lost')}
                              disabled={busyId === `${r.kind}-${r.id}`} data-testid={`lost-perm-${r.kind}-${r.id}`} className="text-red-700 hover:bg-red-50">
                              <XCircle className="h-3 w-3 mr-1" /> Permanently Lost
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* WhatsApp dialog (Item 9) */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>WhatsApp win-back message to {waTarget?.name}</DialogTitle>
            <DialogDescription>Sending to {waTarget?.phone}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <textarea value={waBody} onChange={(e) => setWaBody(e.target.value)} rows={5} maxLength={1500} className="w-full px-3 py-2 border border-slate-300 rounded-md" data-testid="lostlead-wa-body" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setWaOpen(false)}>Cancel</Button>
              <Button onClick={sendWa} disabled={waSending} className="bg-green-600 hover:bg-green-700" data-testid="lostlead-wa-send">
                {waSending ? 'Sending…' : 'Send WhatsApp'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LostLeads;
